import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { Facility } from "../../types/Structure";
import { useToasts } from "../../contexts/toasts";
import { Facilities } from "../../api/Facilities";
import { TailSpin } from "react-loader-spinner";
import FacilityItem from "../../Components/list-items/FacilityItem";
import { useNavigate } from "react-router-dom";

export default function FacilitiesList() {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [facilities, setFacilities] = useState<Facility[]>();
  const [facilityToDelete, setFacilityToDelete] = useState<Facility>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setFacilityToDelete(undefined);
  }, []);

  const initFacilities = useCallback(() => {
    setError(false);
    setFacilities(undefined);
    Facilities.getAdminFacilities().then(
      ({ data }) => {
        setFacilities(data.list);
      },
      () => {
        setFacilities([]);
        setError(true);
        showToast(
          "bookings-retriev-error",
          "Errore nella lista delle Prenotazioni, per favore riprova.",
          "",
          "danger"
        );
      }
    );
  }, [showToast]);

  const reset = useCallback(() => {
    initFacilities();
  }, [initFacilities]);

  const deleteFacility = useCallback(() => {
    setDeleteLoading(true);
    Facilities.deleteFacility(facilityToDelete!.id!).then(
      () => {
        showToast(
          "delete-facility-success",
          "Campo eliminato con successo",
          "",
          "success"
        );
        setDeleteLoading(false);
        reset();
        handleClose();
      },
      () => {
        showToast(
          "delete-facility-error",
          "Errore nella cancellazione del Campo",
          "",
          "danger"
        );
        setDeleteLoading(false);
      }
    );
  }, [showToast, reset, handleClose, facilityToDelete]);

  useEffect(() => {
    initFacilities();
  }, [initFacilities]);

  const openDeleteModal =
    (facility: Facility) => (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      setShowDeleteModal(true);
      setFacilityToDelete(facility);
    };

  const navigateToCreation = () => navigate("/gestione-campi/nuovo");

  const editFacility =
    (facility: Facility) => (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      navigate(`/gestione-campi/${facility.id}/modifica`);
    };

  const onFacilityDetail =
    (facility: Facility) => (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      navigate(`/gestione-campi/${facility.id}`);
    };

  const renderToolbar = () => {
    return (
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">I Tuoi Campi</h4>
        </Col>

        <Col xs={6} md={4} className="text-end">
          <button onClick={navigateToCreation} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Campo</span>
          </button>
        </Col>
      </Row>
    );
  };

  const renderFacilities = () => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return facilities!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessun Campo trovato</span>
      </Alert>
    ) : (
      <Row>
        {facilities!.map((facility, index) => (
          <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={index}>
            <FacilityItem
              facility={facility}
              onEdit={editFacility(facility)}
              onView={onFacilityDetail(facility)}
              onDelete={openDeleteModal(facility)}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const renderAsyncContent = () => {
    return facilities ? (
      renderFacilities()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  };

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      <hr />
      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Cancella Campo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler eliminare{" "}
            <strong>{facilityToDelete?.name || "questo Campo"}</strong> dalla
            tua Struttura?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">Ricorda, l'azione è irreversibile.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={deleteLoading}
            variant="light"
            size="sm"
            className="border"
            onClick={handleClose}
          >
            {deleteLoading ? (
              <TailSpin color="#000" width={20} height={20} />
            ) : (
              <span>Annulla</span>
            )}
          </Button>
          <Button
            disabled={deleteLoading}
            variant="danger"
            size="sm"
            onClick={deleteFacility}
          >
            {deleteLoading ? (
              <TailSpin color="#FFF" width={20} height={20} />
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} />
                <span className="ms-2">Conferma</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
